/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React from 'react';
import { InputGroup, Input } from 'reactstrap';
import { convertValueByDecimal } from '@utils/number';
import { getUtrDecimal } from '@utils/universalTracker';
import { BaseInputProps, HandleValueChange } from './InputProps';
import { Addon } from '@g17eco/types/questionInterfaces';
import { InputHelperText } from './InputHelperText';
import { renderInputGroup } from './InputGroup';
import { isDefined } from '@utils/index';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import UniversalTrackerClassInterface from '../../../../model/UniversalTrackerClassInterface';
import { TableColumn } from '../../../../types/universalTracker';

export interface ValueSliderProps
  extends Partial<Pick<BaseInputProps, 'suffix' | 'unit'>>,
    Pick<BaseInputProps, 'numberScale' | 'handleNumberScaleChange' | 'status' | 'initiativeUtr'> {
  value?: number | string | string[];
  disabled?: boolean;
  placeholder?: string;
  tooltip?: any;
  handleChange: HandleValueChange;
  min?: number;
  max?: number;
  invalidMessage?: string;
  universalTracker: UniversalTrackerClassInterface;
  column?: Pick<TableColumn, 'code'>;
  beforeAddon?: Addon['element'];
  afterAddon?: Addon['element'];
  // TODO: combine with invalidMessage, use array instead [{ text, type: 'warning' | 'error' }]
  warningMessage?: React.ReactNode;
}

/**
 * @param value with decimal ".00" will cut off the decimal part when cast it to number
*/
const getValidatedValue = ({ value, min, max }: { value: string; min?: number; max?: number }) => {
  const numericValue = Number(value);
  if (max !== undefined && numericValue > max) {
    return max;
  }

  if (min !== undefined && numericValue < min) {
    return min;
  }

  return value;
};

export default function ValueSlider(props: ValueSliderProps) {
  const {
    value,
    disabled,
    placeholder,
    tooltip,
    handleChange,
    min = 0,
    max = 100,
    invalidMessage = '',
    universalTracker,
    column,
    beforeAddon,
    afterAddon,
    warningMessage,
    status,
    initiativeUtr,
  } = props;

  const rawUtr = universalTracker.getRaw();
  const decimal = getUtrDecimal(rawUtr, column?.code);

  const onSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const convertedValue = convertValueByDecimal({ decimal, value: e.target.value });
    handleChange({ value: convertedValue, min, max });
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    return handleChange({
      value: value === '' ? undefined : getValidatedValue({ value, min, max }),
      min,
      max,
    });
  };

  return (
    <div className='position-relative row'>
      {beforeAddon || null}
      <div className={props.numberScale ? 'col-5' : 'col-6'}>
        <SimpleTooltip text={tooltip}>
          <Input
            type='range'
            className='h-100'
            value={value === undefined || (typeof value === 'number' && isNaN(value)) || value === '' ? 0 : value}
            disabled={!!disabled}
            onChange={onSliderChange}
            invalid={!!invalidMessage}
            min={min}
            max={max}
          />
        </SimpleTooltip>
      </div>
      <div className={props.numberScale ? 'col-7' : 'col-6'}>
        <InputGroup className={isDefined(decimal) || !!warningMessage ? 'has-validation' : ''}>
          <Input
            type='number'
            disabled={!!disabled}
            placeholder={placeholder}
            value={value === undefined || (typeof value === 'number' && isNaN(value)) || value === '' ? '' : value}
            className={`w-50 ${invalidMessage ? '' : 'styled-input'}`}
            invalid={!!invalidMessage}
            min={min}
            max={max}
            onWheel={(e) => e.currentTarget.blur()}
            onChange={onChange}
          />
          {renderInputGroup({
            suffix: props.suffix,
            unit: props.unit,
            numberScale: props.numberScale,
            handleNumberScaleChange: props.handleNumberScaleChange,
            universalTracker: props.universalTracker,
            isDisabled: () => !!disabled,
            isInvalid: !!invalidMessage,
            handleChange: () => {}, // percentage always has unit is %
            status,
            initiativeUtr,
            column,
          })}
          {disabled ? null : (
            <InputHelperText
              invalidMessage={invalidMessage}
              universalTracker={rawUtr}
              columnCode={column?.code}
              warningMessage={warningMessage}
            />
          )}
        </InputGroup>
      </div>
      {afterAddon || null}
    </div>
  );
}
